/**
 * Contract Year — The anchor entity for Vera Ops
 *
 * One record per group per plan year. Everything hangs off this:
 * solution subscriptions, GI instances, partner IDs, renewals.
 */

/**
 * Plan year workflow status — narrowed 2026-04-11 from a four-state enum
 * (draft / active / confirmed / terminated) to two values. The old `active`
 * and `terminated` semantics are now expressed by other concepts:
 *   - "active" (the year currently in force) → computed from start/end dates
 *     against today (Tier 3 lifecycle: Future / In Force / Past)
 *   - "terminated" → handled at the GROUP level via the Account record's
 *     dts_termdate field (Tier 1 group lifecycle), not at the year level
 *
 * The Dataverse choice column still has the old numeric values 100_000_001
 * and 100_000_003 — the migration script flips every existing `active` row
 * to `confirmed`. Nothing reads or writes `terminated` anymore.
 *
 * See memory: project_plan_year_status_v2.md
 */
export type ContractYearStatus = 'draft' | 'confirmed';

export const CONTRACT_YEAR_STATUS_VALUES: Record<ContractYearStatus, number> = {
  draft: 100_000_000,
  confirmed: 100_000_002,
};

export const CONTRACT_YEAR_STATUS_FROM_VALUE: Record<number, ContractYearStatus> = {
  100_000_000: 'draft',
  // Legacy 100_000_001 (`active`) maps to confirmed so any record that
  // somehow escaped the migration still renders sensibly. The migration
  // script is the source of truth; this is a belt-and-suspenders fallback.
  100_000_001: 'confirmed',
  100_000_002: 'confirmed',
  // Legacy 100_000_003 (`terminated`) also maps to confirmed — termination
  // is now a group-level concept, so any year-level "terminated" rows are
  // re-read as confirmed. Cleanup handled separately.
  100_000_003: 'confirmed',
};

export const CONTRACT_YEAR_STATUS_LABELS: Record<ContractYearStatus, string> = {
  draft: 'Draft',
  confirmed: 'Confirmed',
};

export interface ContractYear {
  id: string;
  name: string;
  accountId: string;
  accountName?: string;
  startDate?: string;
  endDate?: string;
  status: ContractYearStatus;
  previousContractYearId?: string;
  renewalOwnerId?: string;
  renewalOwnerName?: string;
  opportunityId?: string;
  opportunityName?: string;
  terminationReason?: string;
  lives?: number;
  revenue?: number;
  notes?: string;
  /** Audit: who confirmed this plan year, and when. Durable (unlike modifiedby,
   *  which the next edit overwrites). Stamped by useUpdateContractYear on the
   *  draft→confirmed transition; left in place if reverted to draft. */
  confirmedBy?: string;
  confirmedOn?: string;
  createdOn?: string;
  modifiedOn?: string;
}

export interface CreateContractYearInput {
  name?: string;
  accountId: string;
  startDate: string;
  endDate: string;
  status?: ContractYearStatus;
  previousContractYearId?: string;
  renewalOwnerId?: string;
  opportunityId?: string;
  lives?: number;
  revenue?: number;
  notes?: string;
}

export interface UpdateContractYearInput {
  name?: string;
  startDate?: string;
  endDate?: string;
  status?: ContractYearStatus;
  previousContractYearId?: string;
  renewalOwnerId?: string;
  opportunityId?: string;
  terminationReason?: string;
  lives?: number;
  revenue?: number;
  notes?: string;
}

export interface ContractYearQueryParams {
  accountId?: string;
  status?: ContractYearStatus;
  activeOnly?: boolean;
  orderBy?: 'startDate' | 'endDate' | 'modifiedOn';
  orderDirection?: 'asc' | 'desc';
}
